import {IHeaderAngularComp} from "ag-grid-angular";
import {IHeaderParams} from "ag-grid-community";
import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from "@angular/core";
import {HgHedgeMatrixComponent} from "../hg-hedge-matrix.component";
import {DetectMethodChanges, isVoid} from "../../../utils";
import {parseColId} from "../hedge-matrix-col-id";

/**
 * Component replaces standard header to allow copy existing positions into Maven. Offers the choice "Calls/Puts/Both" popup
 */
@Component({
    selector: 'ets-hedge-matrix-total-custom-header',
    template: `
      <div (click)="this.onClick()" #tgt>
        <span *ngIf="this.underlyingDisplay" class="underlying-badge">{{this.underlyingDisplay}}</span>
        P&L TOTAL
      </div>
      <dx-popover [target]="tgt" position="bottom" [visible]="this.popoverVisible"
                  (onHidden)="this.onPopoverHidden()" *ngIf="this.needPopover">
        <div *dxTemplate="let data of 'content'" class="container">
          <div *ngIf="this.hasCalls()" class="choice call" (click)="this.onChoiceClick('Calls')">Calls</div>
          <div *ngIf="this.hasPuts()" class="choice put" (click)="this.onChoiceClick('Puts')">Puts</div>
          <div *ngIf="this.hasPuts() && this.hasCalls()" class="choice put"
               (click)="this.onChoiceClick('Calls & Puts')">Calls&Puts
          </div>
        </div>
      </dx-popover>
    `,
    styles: [
        `
          .container {
              display: flex;
              column-gap: 10px;
              padding: 5px;
          }
          
          .choice {
              border: 3px solid darkgrey;
              padding: 5px;
              color: whitesmoke;
              cursor: pointer;
              font-weight: 800;
              border-radius: 6px;
          }

          .underlying-badge {
              font-size: 9px;
              font-weight: 700;
              color: #aaa;
              background: #333;
              border: 1px solid #555;
              border-radius: 3px;
              padding: 1px 4px;
              letter-spacing: 0.5px;
              white-space: nowrap;
              margin-right: 3px;
          }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HedgeMatrixTotalCustomHeaderComponent implements IHeaderAngularComp {

    constructor(
        private _changeDetector: ChangeDetectorRef
    ) {
    }

    private _etsComponent: HgHedgeMatrixComponent;
    private _expiration: string;
    private _params: IHeaderParams;

    popoverVisible = false;

    get underlyingDisplay(): string {
        if (!this._params || !this._etsComponent) { return undefined; }
        const colId = this._params.column.getColId();
        const sections = this._etsComponent.nonBaseUnderlyingSections;
        if (!sections?.length) { return undefined; }
        const parsed = parseColId(colId);
        const ul = sections.find(s => parsed.silo === s.underlying);
        return ul?.displayName;
    }

    get needPopover() {
        return (this.hasCalls() > 1) || (this.hasPuts() > 1) || (this.hasCalls() > 0 && this.hasPuts() > 0);
    }

    @DetectMethodChanges()
    agInit(params: IHeaderParams): void {
        this._params = params;
        this._etsComponent = params.context?.comp;
        this._expiration = params.context?.expiration;
    }

    @DetectMethodChanges()
    refresh(params: IHeaderParams): boolean {
        this._params = params;
        this._etsComponent = params.context?.comp;
        this._expiration = params.context?.expiration;
        return true;
    }

    @DetectMethodChanges()
    onClick() {

        if (!this._etsComponent.isTextFormatCopyAvailable()) {
            return;
        }


        if (this.needPopover) {
            this.popoverVisible = !this.popoverVisible;
            return
        }

        if (this.hasCalls() > 0) {
            this.onChoiceClick('Calls');
        } else if (this.hasPuts() > 0) {
            this.onChoiceClick('Puts');
        }
    }

    @DetectMethodChanges()
    onPopoverHidden() {
        this.popoverVisible = false;
    }

    @DetectMethodChanges()
    onChoiceClick(choice: 'Calls' | 'Puts'|'Calls & Puts') {
        this.popoverVisible = false;
        setTimeout(async () => {
            await this._etsComponent.onTotalHeaderClicked(this._expiration, choice);
        });
    }

    hasCalls() : number {
        const hasCalls = this._etsComponent.hasHedgesForExpiration(this._expiration, 'Call');
        return hasCalls;
    }

    hasPuts() : number {
        const hasPuts = this._etsComponent.hasHedgesForExpiration(this._expiration, 'Put');
        return hasPuts;
    }
}